import { useEffect, useMemo, useState } from "react";
import {
	type Answer,
	type AttemptStatus,
	demoQuestions,
	getInitialSession,
	isCorrect,
	material,
	type ReviewQuestion,
	type ReviewSession,
	type ShortAnswerGrade,
	sessionKey,
} from "./review";

export function useReviewSession() {
	const [session, setSession] = useState<ReviewSession>(getInitialSession);
	const [selectedAnswer, setSelectedAnswer] = useState<Answer | undefined>();
	const [shortAnswer, setShortAnswer] = useState("");
	const [isGrading, setIsGrading] = useState(false);
	const [gradingError, setGradingError] = useState("");
	const [isExplaining, setIsExplaining] = useState(false);
	const [explanation, setExplanation] = useState("");
	const [chatInput, setChatInput] = useState("");
	const [isChatting, setIsChatting] = useState(false);
	const [chatError, setChatError] = useState("");
	const [failedChatText, setFailedChatText] = useState("");

	const questions = useMemo(() => {
		if (session.mode === "all") return demoQuestions;
		const missedChapters = new Set(
			session.reviewChapterIds ??
				demoQuestions
					.filter((item) => {
						const attempt = session.attempts[item.id];
						return attempt && !isCorrect(attempt.status);
					})
					.map((item) => item.chapterIndex),
		);
		return demoQuestions.filter((item) =>
			missedChapters.has(item.chapterIndex),
		);
	}, [session.attempts, session.mode, session.reviewChapterIds]);

	const currentIndex = Math.min(
		session.currentIndex,
		Math.max(questions.length - 1, 0),
	);
	const current = questions[currentIndex];
	const attempt = current ? session.attempts[current.id] : undefined;
	const completed = Object.keys(session.attempts).length;
	const correctCount = Object.values(session.attempts).filter((item) =>
		isCorrect(item.status),
	).length;
	const reviewCount = completed - correctCount;
	const chapterNumbers = [
		...new Set(questions.map((item) => item.chapterIndex)),
	];
	const currentChapterNumber = current
		? chapterNumbers.indexOf(current.chapterIndex) + 1
		: 0;
	const canSubmit = current
		? current.question.type === "short-answer"
			? shortAnswer.trim().length > 0
			: selectedAnswer !== undefined
		: false;

	useEffect(() => {
		window.localStorage.setItem(sessionKey, JSON.stringify(session));
	}, [session]);

	useEffect(() => {
		if (!current) return;
		const savedAttempt = session.attempts[current.id];
		setSelectedAnswer(savedAttempt?.answer);
		setShortAnswer(
			typeof savedAttempt?.answer === "string" ? savedAttempt.answer : "",
		);
		setGradingError("");
		setExplanation("");
		setChatInput("");
		setChatError("");
		setFailedChatText("");
	}, [current, session.attempts]);

	function saveAttempt(
		answer: Answer,
		status: AttemptStatus,
		feedback?: string,
		misconceptionTags?: string[],
		supportingConcepts?: string[],
		confidence?: number,
	) {
		if (!current) return;
		const submittedAt = Date.now();
		setSession((previous) => {
			const difficulty =
				status === "correct"
					? previous.difficultyMemory
					: [
							{
								keyConcept: current.question.keyConcept,
								chapterTitle: current.chapter.chapterTitle,
								outcome: status,
								misconception: misconceptionTags?.[0],
								lastSeenAt: submittedAt,
							},
							...previous.difficultyMemory.filter(
								(item) => item.keyConcept !== current.question.keyConcept,
							),
						].slice(0, 5);
			return {
				...previous,
				difficultyMemory: difficulty,
				attempts: {
					...previous.attempts,
					[current.id]: {
						answer,
						status,
						feedback,
						misconceptionTags,
						supportingConcepts,
						confidence,
						submittedAt,
					},
				},
			};
		});
	}

	async function submitAnswer() {
		if (!current || !canSubmit || attempt) return;
		setGradingError("");

		if (current.question.type === "multiple-choice") {
			const answer = selectedAnswer as number;
			saveAttempt(
				answer,
				answer === current.question.correctAnswer ? "correct" : "incorrect",
			);
			return;
		}
		if (current.question.type === "true-false") {
			const answer = selectedAnswer as boolean;
			saveAttempt(
				answer,
				answer === current.question.correctAnswer ? "correct" : "incorrect",
			);
			return;
		}

		setIsGrading(true);
		try {
			const response = await fetch("/api/grade-short-answer", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					questionId: current.id,
					answer: shortAnswer.trim(),
					learnerMemory: session.difficultyMemory,
				}),
			});
			if (!response.ok) throw new Error("Unable to grade the response.");
			const grade = (await response.json()) as ShortAnswerGrade;
			saveAttempt(
				shortAnswer.trim(),
				grade.outcome,
				grade.feedback,
				grade.misconceptionTags,
				grade.supportingConcepts,
				grade.confidence,
			);
		} catch {
			setGradingError(
				"We couldn't grade that response. Your answer is still here—please try again.",
			);
		} finally {
			setIsGrading(false);
		}
	}

	function nextQuestion() {
		setSession((previous) => ({
			...previous,
			currentIndex:
				currentIndex === questions.length - 1
					? questions.length
					: previous.currentIndex + 1,
		}));
	}

	async function requestExplanation() {
		if (!current || !attempt) return;
		setIsExplaining(true);
		try {
			const response = await fetch("/api/explain", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					questionId: current.id,
					answer: String(attempt.answer),
					outcome: attempt.status,
					learnerMemory: session.difficultyMemory,
				}),
			});
			if (!response.ok) throw new Error("Unable to generate an explanation.");
			const result = (await response.json()) as { text?: string };
			if (!result.text) throw new Error("The explanation was empty.");
			setExplanation(result.text);
		} catch {
			setExplanation(
				"We couldn't generate an explanation right now. Please try again.",
			);
		} finally {
			setIsExplaining(false);
		}
	}

	async function sendMessage() {
		if (!current || !attempt || isChatting) return;
		const text = (failedChatText || chatInput).trim();
		if (!text) return;

		const questionId = current.id;
		const previousMessages = session.chatByQuestion[questionId] ?? [];
		const isRetry = Boolean(failedChatText);
		const history = previousMessages
			.slice(isRetry ? -6 : -5)
			.map((message) => ({
				role:
					message.role === "student"
						? ("user" as const)
						: ("assistant" as const),
				content: message.text,
			}));
		const conversation = isRetry
			? history
			: [...history, { role: "user" as const, content: text }];

		if (!isRetry) {
			setSession((previous) => ({
				...previous,
				chatByQuestion: {
					...previous.chatByQuestion,
					[questionId]: [
						...(previous.chatByQuestion[questionId] ?? []),
						{ role: "student", text },
					],
				},
			}));
		}
		setChatInput("");
		setChatError("");
		setIsChatting(true);

		try {
			const response = await fetch("/api/chat", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					questionId,
					messages: conversation,
					learnerMemory: session.difficultyMemory,
				}),
			});
			if (!response.ok) throw new Error("Unable to send the message.");
			const result = (await response.json()) as { text?: string };
			const assistantText = result.text;
			if (!assistantText) throw new Error("The response was empty.");
			setSession((previous) => ({
				...previous,
				chatByQuestion: {
					...previous.chatByQuestion,
					[questionId]: [
						...(previous.chatByQuestion[questionId] ?? []),
						{ role: "assistant", text: assistantText },
					],
				},
			}));
			setFailedChatText("");
		} catch {
			setChatError("We couldn't send that message right now.");
			setFailedChatText(text);
		} finally {
			setIsChatting(false);
		}
	}

	function resetProgress() {
		setSession({
			currentIndex: 0,
			mode: "all",
			attempts: {},
			difficultyMemory: [],
			chatByQuestion: {},
		});
		setSelectedAnswer(undefined);
		setShortAnswer("");
		setGradingError("");
		setExplanation("");
		setChatInput("");
		setChatError("");
		setFailedChatText("");
	}

	function reviewAll() {
		setSession((previous) => ({
			currentIndex: 0,
			mode: "all",
			attempts: {},
			difficultyMemory: previous.difficultyMemory,
			chatByQuestion: {},
		}));
	}

	function reviewMissed() {
		const reviewChapterIds = [
			...new Set(
				demoQuestions
					.filter((item) => {
						const attempt = session.attempts[item.id];
						return attempt && !isCorrect(attempt.status);
					})
					.map((item) => item.chapterIndex),
			),
		];
		setSession((previous) => ({
			currentIndex: 0,
			mode: "missed",
			reviewChapterIds,
			attempts: {},
			difficultyMemory: previous.difficultyMemory,
			chatByQuestion: {},
		}));
	}

	return {
		session,
		current,
		currentIndex,
		questions,
		attempt,
		selectedAnswer,
		setSelectedAnswer,
		shortAnswer,
		setShortAnswer,
		isGrading,
		gradingError,
		isExplaining,
		explanation,
		chatInput,
		setChatInput,
		isChatting,
		chatError,
		questionMessages: current ? (session.chatByQuestion[current.id] ?? []) : [],
		completed,
		correctCount,
		reviewCount,
		chapterNumbers,
		currentChapterNumber,
		canSubmit,
		submitAnswer,
		nextQuestion,
		requestExplanation,
		sendMessage,
		reviewAll,
		reviewMissed,
		resetProgress,
	};
}

export type ReviewState = ReturnType<typeof useReviewSession>;
export type { ReviewQuestion };
export { material };
